import React, { useState, useRef } from "react";
import {
  TextField,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Pagination,
  Stack,
  Paper,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip
} from "@mui/material";
import ScaleLoader from "react-spinners/ScaleLoader";
import Visibility from "@mui/icons-material/Visibility";
import useStyles from "../../assets/styles/SupMangComp.style";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import noTableData from "../../assets/images/noTableData.svg";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import useDebounce from "../../utils/useDebounce";
import CustomModel from "../../utils/CustomModel";
import { Notification } from "../../utils/Notification";
import { format } from "date-fns";
import { useGetCandidates, useDeleteCandidate } from "../../hooks/useCandidateManagement";
import useAuth from "../../hooks/useAuth";

const CandidateMangComp = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchText, setSearchText] = React.useState(searchParams.get("search") || "");
  const [profileStatus, setProfileStatus] = React.useState(searchParams.get("status") || "");

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  // Pagination
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);

  // Sync state to URL
  React.useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page);
    if (searchText) params.set("search", searchText);
    if (profileStatus) params.set("status", profileStatus);

    // Only update if params actually changed to avoid infinite loops
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [page, searchText, profileStatus, setSearchParams, searchParams]);

  const handleAlert = () => {
    setAlert(false);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
  };

  const handleNavigate = () => {
    navigate("/candidate_data/addCandidate", { state: { from: location.search } });
  };

  // UserID
  const { userId: userID } = useAuth();

  const debouncedQueryList = useDebounce(searchText, 500);

  // React Query Hook
  const {
    data,
    isPending: showLoader,
    error
  } = useGetCandidates({
    page,
    search: debouncedQueryList,
    status: profileStatus,
    supplierId: userID
  });

  const candidateData = data?.profile || [];
  const listCount = data?.totalCount || 0;

  // Handle errors from React Query
  React.useEffect(() => {
    if (error) {
      setAlertContent(error?.response?.data?.message || "An error occurred fetching candidates");
      setAlertType("error");
      setAlert(true);
    }
  }, [error]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Reviewed', label: 'Reviewed' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Short_listed', label: 'Short Listed' },
    { value: 'Interview_Scheduled', label: 'Interview Scheduled' },
    { value: 'Interview_Cancelled', label: 'Interview Cancelled' },
    { value: 'Staffed', label: 'Staffed' },
    { value: 'Position_on_hold', label: 'Position on Hold' }
  ];
  const parseStatusLabel = (status) => {
    if (!status) return "";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  return (
    <div style={{ marginTop: "20px" }}>
      <Grid
        container
        xs={12}
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <Button
          variant="contained"
          aria-haspopup="true"
          onClick={handleNavigate}
          style={{ background: "#2777AA", width: "214px" }}
        >
          + Add Candidate
        </Button>
      </Grid>
      <Grid container xs={12} style={{ marginTop: "2%" }}>
        <Grid
          item
          xs={6}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <span className={classes.headingText}>Candidate</span>
        </Grid>
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px' }}>
          <TextField
            name="search"
            label="Search by Candidate Name"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '230px' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchText('')}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            autoComplete="off"
          />
          <FormControl variant="outlined" size="small" style={{ width: '220px', backgroundColor: '#fff' }}>
            <InputLabel>Profile Status</InputLabel>
            <Select
              value={profileStatus}
              label="Profile Status"
              onChange={(e) => setProfileStatus(e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

      </Grid>
      <Grid container xs={12} className={classes.tableGrid}>
        {showLoader ? (
          <div
            style={{
              display: "flex",
              height: 400,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
          </div>
        ) : candidateData?.length === 0 ? (
          <div
            style={{
              height: 300,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img src={noTableData} width={60} alt="No Data" />
            <div
              style={{
                marginTop: 5,
                fontWeight: "500",
                fontSize: 25,
                fontFamily: "'Noto Sans', sans-serif",
              }}
            >
              There are no recent Candidate to show.
            </div>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell className={classes.tableHeadCell}>
                    Job Code
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>Name</TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Current Address
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Budget
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Budget EOM
                  </TableCell>
                  {/* <TableCell className={classes.tableHeadCell}>
                    Feedback
                  </TableCell> */}
                  <TableCell className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Status Update Date
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <>
                <TableBody className={classes.noHorizontalBorderTableRow}>
                  {candidateData.map((val) => {
                    return (
                      <TableRow key={val.profile_id}>
                        <TableCell className={classes.tableBodyCell}>
                          {val.job_code}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {val.candidate_name}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {val.candidate_current_location}
                        </TableCell>
                        <TableCell>{val.budget}</TableCell>
                        <TableCell>{val.budget_eom}</TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Tooltip
                            title={parseStatusLabel(val?.detailed_status) || 'No detailed status'}
                            arrow
                            placement="top"
                          >
                            <span> {/* Tooltip requires a single child with onMouseOver support */}
                              <Chip
                                label={parseStatusLabel(val?.detailed_status)}
                                size="small"
                                color="default"
                                sx={{ cursor: 'help' }} // Visual hover indicator
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {val.status_date ? format(new Date(val.status_date), "dd-MM-yyyy") : "-"}
                        </TableCell>
                        <TableCell>
                          <MoreMenu
                            details={val}
                            setAlertContent={setAlertContent}
                            setAlertType={setAlertType}
                            setAlert={setAlert}
                            navigate={navigate}
                            location={location}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            </Table>
            <Stack
              spacing={2}
              sx={{
                padding: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Pagination
                classes={{
                  root: classes.pagination,
                }}
                count={Math.ceil(listCount / 10)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Stack>
          </TableContainer>
        )}
      </Grid>
      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlert}
      />
    </div>
  );
};

export default CandidateMangComp;


function MoreMenu({
  details,
  setAlertContent,
  setAlertType,
  setAlert,
  navigate,
  location,
}) {
  const [delOpen, setDelOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // React Query Mutation
  const { mutate: deleteCandidate } = useDeleteCandidate();

  const refDel = useRef(delOpen);

  const handleDialogInfo = () => {
    setAnchorEl(null);
  };

  const handleDialogdel = () => {
    setDelOpen(!delOpen);
  };
  const open = Boolean(anchorEl);

  const handleSupplierDelete = () => {
    deleteCandidate(details.profile_id, {
      onSuccess: (data) => {
        handleDialogdel();
        setAlertContent(data?.message || "Candidate deleted successfully");
        setAlertType("success");
        setAlert(true);
      },
      onError: (error) => {
        setAlertContent(error?.response?.data?.message || "Error deleting candidate");
        setAlertType("error");
        setAlert(true);
      }
    });
  };

  // ✅ SAME CONDITIONAL LOGIC FROM AdminStatusUpdateForm
  const getFeedbackFieldsForStatus = (status) => {
    const profileOnlyStages = [
      "Vendor_Submission",
      "Internal_Evaluation",
      "Internal_Screening",
      "Internal_Feedback",
      "Submitted_To_Client",
      "Client_Evaluation"
    ];

    const interviewOnlyStages = [
      "L1_Round",
      "L2_Round",
      "L3_Round"
    ];

    const bothFeedbackStages = [
      "Rejected",
      "Interview_Cancelled",
      "Position_on_hold"
    ];

    if (profileOnlyStages.includes(status)) {
      return 'profile';
    } else if (interviewOnlyStages.includes(status)) {
      return 'interview';
    } else if (bothFeedbackStages.includes(status)) {
      return 'both';
    }

    return null;
  };

  const parseStatusLabel = (status) => {
    if (!status) return "";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // ✅ Calculate feedback type for conditional display
  const feedbackType = getFeedbackFieldsForStatus(details?.detailed_status);

  return (
    <>
      <IconButton
        onClick={() => {
          navigate("/candidate_data/Viewcandidate", {
            state: { CandidateDetails: details, from: location.search },
          });
        }}
      >
        <Visibility sx={{ color: "#2777AA" }} />
      </IconButton>

      <IconButton
        onClick={() => {
          navigate("/candidate_data/editcandidate", {
            state: { CandidateDetails: details, from: location.search },
          });
        }}
      >
        <EditIcon sx={{ color: "#2777AA" }} />
      </IconButton>

      {/* ✅ Updated condition - uses detailed_status OR existing feedbacks */}
      {(details.profile_feedback ||
        details.interview_feedback ||
        details.candidate_status ||
        details.detailed_status) && (
          <IconButton
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
          >
            <InfoIcon sx={{ color: "#2777AA" }} />
          </IconButton>
        )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleDialogInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "block",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <p>
              <b>Date</b>: {details.updatedAt ? format(new Date(details.updatedAt), "yyyy-MM-dd") : ""}
            </p>
            <p>
              <b>Status</b>: {parseStatusLabel(details?.detailed_status) || "Update Soon"}
            </p>

            {details.status_group === 'Interview_Scheduled' && (
              <>
                <p>
                  <b>Interview Scheduled Date</b>:{" "}
                  {details.interview_datetime || "Update Soon"}
                </p>
                <p>
                  <b>Interview Link</b>:{" "}
                  {details.interview_link ? (
                    <a
                      href={details.interview_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2777AA",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                      }}
                    >
                      {details.interview_link}
                    </a>
                  ) : (
                    "Update Soon"
                  )}
                </p>
              </>
            )}

            {/* ✅ CONDITIONAL FEEDBACK DISPLAY - SAME LOGIC */}
            {feedbackType === 'profile' || feedbackType === 'both' ? (
              <p>
                <b>Profile Feedback</b>:{" "}
                {details.profile_feedback || "Update Soon"}
              </p>
            ) : null}

            {feedbackType === 'interview' || feedbackType === 'both' ? (
              <p>
                <b>Interview Feedback</b>:{" "}
                {details.interview_feedback || "Update Soon"}
              </p>
            ) : null}

          </div>
        </Grid>
      </Popover>

      <CustomModel
        handleDialog={handleDialogdel}
        open={delOpen}
        title="Delete Job"
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "24px",
          }}
        >
          Do you want to delete the Candidate Profile?
        </div>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleSupplierDelete(details?.job_id)}
              style={{ background: "#2777AA" }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDialogdel()}
              style={{ background: "#2777AA" }}
            >
              NO
            </Button>
          </div>
        </Grid>
      </CustomModel>

      <IconButton
        ref={refDel}
        onClick={() => {
          setDelOpen(true);
        }}
      >
        <DeleteIcon sx={{ color: "red" }} />
      </IconButton>
    </>
  );
}

